import {
  Type, Image as ImageIcon, Square, Circle, Trash2, Copy, Grid3X3, 
  ZoomIn, ZoomOut, Scissors, FileUp, List, ArrowUpToLine, ArrowDownToLine,
  ArrowUp, ArrowDown, Maximize, Stamp, MousePointer2, Layers, FolderInput
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export function MoodBoardToolbar({
  activeTool,
  setActiveTool,
  addTextElement,
  setShowClipsPanel,
  fileInputRef,
  uploadingImage,
  addShapeElement,
  setShowShapeLibrary,
  shapeFillColor,
  setShapeFillColor,
  showGrid,
  setShowGrid,
  showSpecTags,
  setShowSpecTags,
  showLegend,
  setShowLegend,
  showWatermark,
  setShowWatermark,
  zoom,
  setZoom,
  fitToView,
  selectedElement,
  selectedId,
  startCutout,
  bringToFront,
  bringForward,
  sendBackward,
  sendToBack,
  duplicateElement,
  deleteElement,
  onPullFromProject,
}) {
  return (
    <div className="bg-white border-b border-border px-3 py-1.5 flex items-center gap-2 flex-wrap">
      {/* Select & Add Tools */}
      <div className="flex items-center gap-0.5">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={activeTool === 'select' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setActiveTool('select')}
                data-testid="select-tool-btn"
              >
                <MousePointer2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Select & Move</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <div className="w-px h-5 bg-border mx-1" />
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0" 
                onClick={addTextElement}
                data-testid="add-text-btn"
              >
                <Type className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Add Text</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0" 
                onClick={() => setShowClipsPanel(true)}
                data-testid="add-from-clips-btn"
              >
                <ImageIcon className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Add from Clips</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0" 
                onClick={() => onPullFromProject && onPullFromProject()}
                disabled={!onPullFromProject}
                data-testid="pull-from-project-btn"
              >
                <FolderInput className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Pull image from this project (field / desktop / email)</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0" 
                onClick={() => fileInputRef.current?.click()} 
                disabled={uploadingImage}
                data-testid="upload-image-btn"
              >
                <FileUp className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Upload Image</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      {/* Shapes */}
      <div className="flex items-center gap-0.5">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0" 
                onClick={() => addShapeElement('rectangle')}
                data-testid="add-rectangle-btn"
              >
                <Square className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Rectangle</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0" 
                onClick={() => addShapeElement('ellipse')}
                data-testid="add-ellipse-btn"
              >
                <Circle className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Ellipse</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowShapeLibrary(true)}
                data-testid="shape-library-btn"
              >
                <Layers className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>More Shapes</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <input
          type="color"
          value={shapeFillColor}
          onChange={(e) => setShapeFillColor(e.target.value)}
          className="w-6 h-6 rounded cursor-pointer border border-border ml-1"
          title="Fill Color"
        />
      </div>
      
      <div className="w-px h-5 bg-border" />
      
      {/* View Controls */}
      <div className="flex items-center gap-0.5">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={showGrid ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowGrid(!showGrid)}
                data-testid="toggle-grid-btn"
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle Grid</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={showSpecTags ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowSpecTags(!showSpecTags)}
                data-testid="toggle-spec-tags-btn"
              >
                <span className="text-xs font-bold">S#</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle Spec Tags</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={showLegend ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowLegend(!showLegend)}
                data-testid="toggle-legend-btn"
              >
                <List className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle Legend</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={showWatermark ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowWatermark(!showWatermark)}
                data-testid="toggle-watermark-btn"
              >
                <Stamp className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle Logo Watermark</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      <div className="w-px h-5 bg-border" />
      
      {/* Zoom */}
      <div className="flex items-center gap-1">
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-8 w-8 p-0" 
          onClick={() => setZoom(z => Math.max(0.25, z - 0.1))}
          data-testid="zoom-out-btn"
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-xs w-10 text-center">{Math.round(zoom * 100)}%</span>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-8 w-8 p-0" 
          onClick={() => setZoom(z => Math.min(2, z + 0.1))}
          data-testid="zoom-in-btn"
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0" 
                onClick={fitToView}
                data-testid="fit-to-view-btn"
              >
                <Maximize className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Fit to View</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      {/* Selected element actions */}
      {selectedElement && (
        <>
          <div className="w-px h-5 bg-border" />
          <div className="flex items-center gap-0.5">
            {selectedElement.element_type === 'image' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0" 
                      onClick={() => startCutout(selectedId)}
                      data-testid="cutout-tool-btn"
                    >
                      <Scissors className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Cutout Tool</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0" 
                    onClick={() => bringToFront(selectedId)}
                    data-testid="bring-to-front-btn"
                  >
                    <ArrowUpToLine className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Bring to Front</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0" 
                    onClick={() => bringForward(selectedId)}
                    data-testid="bring-forward-btn"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Bring Forward</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0" 
                    onClick={() => sendBackward(selectedId)}
                    data-testid="send-backward-btn"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Send Backward</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0" 
                    onClick={() => sendToBack(selectedId)}
                    data-testid="send-to-back-btn"
                  >
                    <ArrowDownToLine className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Send to Back</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0" 
                    onClick={() => duplicateElement(selectedId)}
                    data-testid="duplicate-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Duplicate</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0 text-destructive" 
                    onClick={() => deleteElement(selectedId)}
                    data-testid="delete-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </>
      )}
    </div>
  );
}
